import { TRPCClientError } from '@trpc/client'

import type { GameStatusData, SubmitActionInput } from '@/lib/types'

export type RoleActionNotice
  = 'none'
    | 'checking'
    | 'processing'
    | 'failed'
    | 'credits'
    | 'unknown'
    | 'stale'

export interface OwnedRoleAction {
  actionId: string
  playerId: string
}

export interface PreparedRoleAction<TReceipt> {
  responses: SubmitActionInput['responses']
  receipt: TReceipt
}

export interface AcceptedRoleActionReceipt<TReceipt> extends OwnedRoleAction {
  value: TReceipt
}

interface RoleActionLifecycleState<TReceipt> {
  notice: RoleActionNotice
  receipt: AcceptedRoleActionReceipt<TReceipt> | null
}

export interface RoleActionAttemptDependencies<
  TInput extends OwnedRoleAction,
  TDraft,
  TReceipt,
> {
  readStatus: () => Promise<GameStatusData>
  readCredit: () => Promise<number>
  readInput: (data: GameStatusData) => TInput | null
  prepare: (
    freshInput: TInput,
    draft: TDraft,
    freshStatus: GameStatusData,
  ) => PreparedRoleAction<TReceipt> | null
  submit: (responses: SubmitActionInput['responses']) => Promise<unknown>
  converge: () => Promise<unknown>
  onAccepted?: (input: TInput, receipt: TReceipt) => void
  onReceiptChange?: (receipt: TReceipt | null, previous?: TReceipt) => void
}

export interface RoleActionLifecycleDependencies<
  TInput extends OwnedRoleAction,
  TDraft,
  TReceipt,
> {
  snapshot: () => RoleActionAttemptDependencies<TInput, TDraft, TReceipt>
  onChange?: () => void
  initialGameId?: string
  initialInput?: TInput | null
  initialReceipt?: AcceptedRoleActionReceipt<TReceipt> | null
  initialReceiptCleanup?: () => void
}

const DEFINITE_REJECTION_CODES = new Set([
  'BAD_REQUEST',
  'FORBIDDEN',
  'UNAUTHORIZED',
  'CONFLICT',
  'PRECONDITION_FAILED',
])

export function isSameOwnedAction(
  left: OwnedRoleAction | null,
  right: OwnedRoleAction | null,
) {
  return !!left
    && !!right
    && left.actionId === right.actionId
    && left.playerId === right.playerId
}

function classifySubmissionError(error: unknown): 'credits' | 'failed' | 'unknown' {
  if (!(error instanceof TRPCClientError))
    return 'unknown'
  if (
    error.data?.code === 'FORBIDDEN'
    && error.message === 'insufficient_credits_to_continue'
  ) {
    return 'credits'
  }
  return DEFINITE_REJECTION_CODES.has(error.data?.code ?? '')
    ? 'failed'
    : 'unknown'
}

export function createRoleActionLifecycle<
  TInput extends OwnedRoleAction,
  TDraft,
  TReceipt,
>(dependencies: RoleActionLifecycleDependencies<TInput, TDraft, TReceipt>) {
  let state: RoleActionLifecycleState<TReceipt> = {
    notice: 'none',
    receipt: dependencies.initialReceipt ?? null,
  }
  let receiptCleanup: (() => void) | null
    = dependencies.initialReceipt ? dependencies.initialReceiptCleanup ?? null : null
  let inFlightGeneration: number | null = null
  let generation = 0
  let contextGameId = dependencies.initialGameId ?? null
  let contextAction: OwnedRoleAction | null = dependencies.initialInput
    ?? dependencies.initialReceipt
    ?? null
  let contextHasInput = !!dependencies.initialInput

  function update(next: Partial<RoleActionLifecycleState<TReceipt>>) {
    state = { ...state, ...next }
    dependencies.onChange?.()
  }

  function clearReceipt() {
    const cleanup = receiptCleanup
    receiptCleanup = null
    update({ receipt: null })
    cleanup?.()
  }

  function reset() {
    const cleanup = receiptCleanup
    receiptCleanup = null
    state = { notice: 'none', receipt: null }
    cleanup?.()
    dependencies.onChange?.()
  }

  function isCurrent(attemptGeneration: number) {
    return attemptGeneration === generation
  }

  function applyUnreconciledFailure(
    classification: 'credits' | 'failed' | 'unknown',
  ) {
    if (classification === 'failed')
      clearReceipt()
    update({
      notice: classification === 'failed'
        ? 'failed'
        : classification === 'credits' ? 'credits' : 'unknown',
    })
  }

  function blocked(input: TInput | null, statusError: boolean) {
    return statusError
      || inFlightGeneration === generation
      || (!!input && isSameOwnedAction(input, state.receipt))
      || ['checking', 'processing', 'credits', 'unknown', 'stale']
        .includes(state.notice)
  }

  async function refresh(input: TInput | null, afterFailure = false) {
    const attempt = dependencies.snapshot()
    const attemptGeneration = generation
    const checkingCredits = state.notice === 'credits'
    const recoveringUncertain = state.notice === 'unknown'
      || state.notice === 'stale'
    update({ notice: 'checking' })
    try {
      const fresh = await attempt.readStatus()
      if (!isCurrent(attemptGeneration))
        return
      const balance = checkingCredits ? await attempt.readCredit() : null
      if (!isCurrent(attemptGeneration))
        return
      await attempt.converge()
      if (!isCurrent(attemptGeneration))
        return
      if (!input || !isSameOwnedAction(input, attempt.readInput(fresh))) {
        update({ notice: input ? 'stale' : 'none' })
        return
      }
      if (checkingCredits)
        clearReceipt()
      if (checkingCredits && !(balance !== null && balance > 0)) {
        update({ notice: 'credits' })
        return
      }
      if (afterFailure)
        clearReceipt()
      if (recoveringUncertain && state.receipt && !afterFailure) {
        update({ notice: 'unknown' })
        return
      }
      update({ notice: afterFailure ? 'failed' : 'none' })
    }
    catch {
      if (isCurrent(attemptGeneration))
        update({ notice: checkingCredits ? 'credits' : 'unknown' })
    }
  }

  async function confirm(
    input: TInput | null,
    draft: TDraft,
    statusError = false,
  ) {
    if (!input || blocked(input, statusError))
      return false

    const attempt = dependencies.snapshot()
    const attemptGeneration = generation
    inFlightGeneration = attemptGeneration
    update({ notice: 'checking' })

    try {
      let fresh: GameStatusData
      try {
        fresh = await attempt.readStatus()
      }
      catch {
        if (isCurrent(attemptGeneration))
          update({ notice: 'failed' })
        return false
      }

      if (!isCurrent(attemptGeneration))
        return false

      const freshInput = attempt.readInput(fresh)
      const submission = freshInput && isSameOwnedAction(input, freshInput)
        ? attempt.prepare(freshInput, draft, fresh)
        : null
      if (!freshInput || !submission) {
        update({ notice: 'stale' })
        try {
          await attempt.converge()
        }
        catch {}
        return false
      }

      if (!isCurrent(attemptGeneration))
        return false

      const receipt = {
        actionId: input.actionId,
        playerId: input.playerId,
        value: submission.receipt,
      }
      receiptCleanup = () => attempt.onReceiptChange?.(null, submission.receipt)
      update({ receipt })
      attempt.onReceiptChange?.(submission.receipt)

      try {
        await attempt.submit(submission.responses)
      }
      catch (error) {
        const classification = classifySubmissionError(error)
        try {
          const reconciled = await attempt.readStatus()
          await attempt.converge()
          if (!isCurrent(attemptGeneration))
            return false
          const stillCurrent = isSameOwnedAction(
            input,
            attempt.readInput(reconciled),
          )
          if (stillCurrent && classification !== 'unknown')
            clearReceipt()
          update({ notice: stillCurrent ? classification : 'stale' })
        }
        catch {
          if (isCurrent(attemptGeneration))
            applyUnreconciledFailure(classification)
        }
        return false
      }

      attempt.onAccepted?.(input, submission.receipt)
      if (isCurrent(attemptGeneration))
        update({ notice: 'processing' })
      try {
        await attempt.converge()
      }
      catch {}
      return true
    }
    finally {
      if (inFlightGeneration === attemptGeneration)
        inFlightGeneration = null
    }
  }

  return {
    blocked,
    clearReceipt,
    confirm,
    edit: () => update({ notice: 'none' }),
    getContext: () => ({ action: contextAction, gameId: contextGameId }),
    getState: () => state,
    refresh,
    replaceContext(gameId: string, input: TInput | null) {
      if (contextGameId === null) {
        contextGameId = gameId
        contextAction = input
        contextHasInput = !!input
        return
      }

      if (contextGameId !== gameId) {
        generation += 1
        contextGameId = gameId
        contextAction = input
        contextHasInput = !!input
        reset()
        return
      }

      if (input && !isSameOwnedAction(input, contextAction)) {
        generation += 1
        contextAction = input
        contextHasInput = true
        reset()
        return
      }

      if (!input && contextHasInput) {
        generation += 1
        contextHasInput = false
        if (!state.receipt && state.notice !== 'none')
          update({ notice: 'none' })
        return
      }

      if (input && !contextHasInput) {
        generation += 1
        contextHasInput = true
      }
    },
  }
}
